import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../context/AuthContext";
import { ClientProps } from "../interfaces/interfaces";

export const useAssignedClientSelection = () => {
  const {
    clientId,
    clients,
    idRole,
    selectedClientId,
    setSelectedClientId,
  } = useContext(AuthContext);
  const [currentAssignments, setCurrentAssignments] = useState<
    ClientProps[] | null
  >(null);

  useEffect(() => {
    if (idRole !== 3) return;
    const controller = new AbortController();
    const loadAssignments = async () => {
      try {
        const response = await fetch(
          String(process.env.REACT_APP_DEV_API) +
            "/user-client-assignments",
          {
            signal: controller.signal,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("info"),
            },
          },
        );
        const result = await response.json();
        if (response.ok && result.success) {
          setCurrentAssignments(result.data as ClientProps[]);
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setCurrentAssignments(null);
        }
      }
    };
    loadAssignments();
    return () => controller.abort();
  }, [idRole]);

  const clientOptions = useMemo<ClientProps[]>(
    () => {
      if (currentAssignments !== null) return currentAssignments;
      return clients.length
        ? clients
        : clientId
          ? [{ id: clientId, name: "Client" }]
          : [];
    },
    [clientId, clients, currentAssignments],
  );

  useEffect(() => {
    const selectionIsAssigned = clientOptions.some(
      (client) => client.id === selectedClientId,
    );
    if (!selectionIsAssigned) {
      setSelectedClientId(clientOptions[0]?.id ?? null);
    }
  }, [clientOptions, selectedClientId, setSelectedClientId]);

  const effectiveSelectedClientId = clientOptions.some(
    (client) => client.id === selectedClientId,
  )
    ? selectedClientId
    : clientOptions[0]?.id ?? null;

  return {
    clientOptions,
    selectedClientId: effectiveSelectedClientId,
    setSelectedClientId,
    showClientFilter: clientOptions.length > 1,
  };
};
